import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Gem, Loader2, X, Layers, Crosshair, Pin, MapPin, Satellite } from "lucide-react";

const COMMODITIES = [
  "Gold", "Silver", "PGE", "Copper", "Lead", "Zinc", "Nickel",
  "Lithium", "Cobalt", "Graphite", "Manganese", "Vanadium",
  "Rare Earth Elements", "Uranium", "Tin", "Tungsten",
  "Tantalum-Niobium", "Titanium", "Molybdenum", "Antimony",
  "Ilmenite", "Rutile", "Leucoxene", "Zircon", "Monazite",
  "Iron Ore", "Chromite",
  "Bauxite", "Limestone", "Dolomite", "Coal", "Phosphate", "Barite", "Fluorite",
  "Kaolin", "Bentonite", "Magnesite", "Silica Sand", "Feldspar", "Talc", "Diatomite", "Mica",
  "Wollastonite", "Vermiculite", "Perlite", "Zeolite", "Sillimanite", "Kyanite", "Andalusite",
  "Pyrophyllite", "Nepheline Syenite", "Sulphur",
  "Soda Ash", "Potash", "Salt", "Gypsum", "Borates",
  "Diamond", "Tanzanite", "Ruby & Sapphire", "Emerald", "Garnet",
];

const TIER = {
  High: { c: "#E11D48", bg: "bg-rose-50", tx: "text-rose-700", bd: "border-rose-200" },
  Medium: { c: "#F59E0B", bg: "bg-red-50", tx: "text-red-700", bd: "border-red-200" },
  Low: { c: "#38BDF8", bg: "bg-sky-50", tx: "text-sky-700", bd: "border-sky-200" },
};

// Adaptive HM-tonnage units so small AOIs (t/kt) and large HMS strand-plains (Mt) both read cleanly.
function fmtHMRange(lo, hi) {
  if (lo == null && hi == null) return "-";
  lo = lo || 0; hi = hi || 0;
  if (hi >= 1) return `${lo.toFixed(2)}–${hi.toFixed(2)} Mt`;
  if (hi >= 0.001) return `${(lo * 1000).toFixed(1)}–${(hi * 1000).toFixed(1)} kt`;
  if (hi > 0) return `${Math.round(lo * 1e6).toLocaleString()}–${Math.round(hi * 1e6).toLocaleString()} t`;
  return "negligible";
}

function ZoneChip({ tier, z }) {
  const t = TIER[tier];
  return (
    <div className={`flex-1 rounded-lg border ${t.bd} ${t.bg} px-3 py-2`} data-testid={`zone-${tier.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: t.c }}>
        <span className="h-2 w-2 rounded-full" style={{ background: t.c }} />{tier}
      </div>
      <div className={`text-lg font-extrabold ${t.tx}`} style={{ fontFamily: "Manrope, sans-serif" }}>{z.pct}%</div>
      {z.km2 != null && <div className="text-[10px] text-slate-500">{z.km2} km²</div>}
    </div>
  );
}

function CombinedCard({ combined, onZoom }) {
  const z = combined.zones || {};
  const maps = combined.maps || {};
  const leaders = combined.leaders || [];
  const tc = combined.target_counts || {};
  const tiles = [
    ["prospectivity", "Combined Prospectivity (per-cell max)"],
    ["leader", "Leading Mineral per Zone"],
    ["target_zones", "Combined Target Zones"],
  ];
  return (
    <div className="bg-white border-2 border-[#C20511]/50 rounded-xl shadow-sm overflow-hidden" data-testid="mineral-combined-card">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-[#0B1420] flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers size={16} style={{ color: "#C20511" }} />
          <span className="font-extrabold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Combined Multi-Mineral Prospectivity</span>
          <span className="text-[11px] text-slate-300">· {(combined.minerals || []).length} minerals</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold">
          {["High", "Medium", "Low"].map((t) => (
            <span key={t} className={`px-2 py-0.5 rounded-full border ${TIER[t].bd} ${TIER[t].bg} ${TIER[t].tx}`}>{tc[t] || 0} {t}</span>
          ))}
        </div>
      </div>
      <div className="p-5 grid lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div className="flex gap-2">
            <ZoneChip tier="High" z={z.high || { pct: 0 }} />
            <ZoneChip tier="Medium" z={z.medium || { pct: 0 }} />
            <ZoneChip tier="Low" z={z.low || { pct: 0 }} />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Leading mineral breakdown</div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-xs" data-testid="mineral-combined-leaders">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Mineral</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Leads (% AOI)</th>
                    <th className="text-right px-2 py-1.5 font-semibold">High-zone lead</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.map((l) => (
                    <tr key={l.mineral} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-medium text-slate-700">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm mr-1.5 align-middle" style={{ background: l.color }} />{l.label}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-700">{l.lead_pct}%</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-800 font-bold">{l.high_lead_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Per-cell maximum prospectivity across all selected minerals; the "leading mineral" is the strongest commodity in each cell.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mineral-combined-maps">
          {tiles.map(([k, label]) => (
            maps[k] ? (
              <figure key={k} className="cursor-zoom-in" onClick={() => onZoom(maps[k])} data-testid={`mineral-combined-map-${k}`}>
                <img src={maps[k]} alt={label} className="w-full rounded-lg border border-slate-200 hover:border-[#C20511] transition-colors" />
                <figcaption className="text-[10px] text-slate-500 mt-1 text-center">{label}</figcaption>
              </figure>
            ) : null
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MineralAnalysisDialog({ open, onOpenChange, projectId, area }) {
  const aid = area?.area_id;
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState(null);
  const [minerals, setMinerals] = useState({});
  const [combined, setCombined] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [compare, setCompare] = useState(false);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [pinned, setPinned] = useState([]);
  const [pinBusy, setPinBusy] = useState(false);
  const [country, setCountry] = useState(null);
  const [provenance, setProvenance] = useState("");
  const pollRef = useRef(null);
  const prefilled = useRef(false);

  const load = useCallback(async () => {
    if (!projectId || !aid) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/areas/${aid}/minerals`);
      setStatus(data.status);
      setMinerals(data.minerals || {});
      setCombined(data.combined || null);
      setPinned(data.compare || []);
      setCountry(data.country || null);
      setProvenance(data.provenance || "");
      if ((data.selected || []).length) {
        setSelected((s) => (s.length ? s : data.selected));
        prefilled.current = true;
      } else if (!prefilled.current && !Object.keys(data.minerals || {}).length && (data.preset || []).length) {
        // Auto-suggest + pre-tick the detected country's local commodities (user can adjust).
        setSelected((s) => (s.length ? s : data.preset));
        prefilled.current = true;
      }
      if (data.status !== "processing" && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    } catch (e) { /* ignore transient */ }
  }, [projectId, aid]);

  useEffect(() => {
    if (open) { setZoom(null); prefilled.current = false; load(); }
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [open, load]);

  const toggle = (m) =>
    setSelected((s) => (s.includes(m) ? s.filter((x) => x !== m) : [...s, m]));

  const analyze = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/areas/${aid}/minerals/analyze`, { minerals: selected });
      setStatus("processing");
      toast.info(`Analysing ${selected.length} mineral${selected.length === 1 ? "" : "s"} - per-mineral maps, tables and corridor summaries will appear shortly`);
      clearInterval(pollRef.current);
      pollRef.current = setInterval(load, 4000);
    } catch (e) { setBusy(false); toast.error(apiError(e)); }
  };

  const pinCompare = async (a, b) => {
    if (!a || !b || a === b) return;
    setPinBusy(true);
    try {
      await api.post(`/projects/${projectId}/areas/${aid}/minerals/compare`, { pair: [a, b] });
      setPinned([a, b]);
      toast.success(`Pinned ${minerals[a]?.label || a} vs ${minerals[b]?.label || b} for the report comparison page`);
    } catch (e) { toast.error(apiError(e)); } finally { setPinBusy(false); }
  };

  const names = Object.keys(minerals);
  const processing = status === "processing" || busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] w-[92vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden" data-testid="mineral-analysis-dialog">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-[#0B1420] shrink-0">
          <DialogTitle className="text-sm font-bold text-white flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Gem size={16} style={{ color: "#C20511" }} />
            Multi-Mineral Analysis - {area?.name || aid}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Select any number of commodities to generate per-mineral prospectivity, High/Medium/Low target zones, ranked target tables and corridor potential for this licence area.
          </DialogDescription>
          <button data-testid="mineral-close" onClick={() => onOpenChange(false)}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-300 hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-slate-50">
          {/* selector */}
          <div className="bg-white border-b border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Select minerals <span className="text-slate-400 normal-case font-medium">· unlimited</span>
              </div>
              {selected.length > 0 && (
                <span className="text-[11px] font-semibold text-white bg-[#FFFFFF]/20 border border-[#C20511]/40 rounded-full px-2.5 py-0.5" data-testid="mineral-selected-count">
                  {selected.length} selected
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {country && (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#FFFFFF]/20 border border-[#C20511]/40 rounded-full px-3 py-1" data-testid="mineral-detected-country">
                  <MapPin size={13} style={{ color: "#C20511" }} />
                  Jurisdiction auto-detected from boundary: <b>{country}</b>
                </span>
              )}
              <button data-testid="mineral-clear" onClick={() => setSelected([])}
                className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2">Clear selection</button>
            </div>
            {country && (
              <div className="text-[11px] text-slate-500 mb-2" data-testid="mineral-detected-note">
                Locally relevant commodities for <b className="text-slate-700">{country}</b> are pre-selected - adjust freely.
              </div>
            )}
            <div className="flex flex-wrap gap-2" data-testid="mineral-picker">
              {COMMODITIES.map((m) => {
                const on = selected.includes(m);
                return (
                  <button key={m} data-testid={`mineral-opt-${m.replace(/\s+/g, "-")}`} onClick={() => toggle(m)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${on
                      ? "bg-[#0B1420] text-white border-[#0B1420]"
                      : "bg-white text-slate-600 border-slate-300 hover:border-[#C20511]"}`}>
                    {on && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: "#C20511" }} />}
                    {m}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <Button onClick={analyze} disabled={processing || !selected.length}
                data-testid="mineral-analyze-btn"
                className="h-10 px-6 font-semibold text-white" style={{ background: "#C20511" }}>
                {processing ? <><Loader2 size={16} className="mr-2 animate-spin" />Analysing…</>
                  : <><Crosshair size={16} className="mr-2" />Analyse {selected.length || ""} Mineral{selected.length === 1 ? "" : "s"}</>}
              </Button>
              <span className="text-xs text-slate-400">Re-fuses stored imagery per mineral - no re-download.</span>
            </div>
            <div className="mt-3 flex items-start gap-1.5 text-[11px] text-slate-400" data-testid="mineral-provenance">
              <Satellite size={13} className="mt-0.5 shrink-0" style={{ color: "#C20511" }} />
              <span>{provenance || "Sentinel-2 MSI L2A (10 m, most-recent cloud-free scenes) · Copernicus DEM GLO-30 (30 m) · Sentinel-1 SAR relief - highest-resolution free public sources prioritised."}</span>
            </div>
          </div>

          {names.length >= 2 && (
            <div className="px-5 pt-4" data-testid="mineral-compare-bar">
              <button data-testid="mineral-compare-toggle" onClick={() => setCompare((c) => !c)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1420] bg-white border border-slate-300 rounded-lg px-3 py-2 hover:border-[#C20511] transition-colors">
                <Layers size={15} style={{ color: "#C20511" }} />
                {compare ? "Hide comparison" : "Compare two minerals side-by-side"}
              </button>
            </div>
          )}

          {compare && names.length >= 2 && (() => {
            const a = cmpA && minerals[cmpA] ? cmpA : names[0];
            const b = cmpB && minerals[cmpB] ? cmpB : (names[1] || names[0]);
            const tiers = [["High", "high"], ["Medium", "medium"], ["Low", "low"]];
            const fmt = (x) => (x && x.km2 != null ? `${x.km2} km² · ${x.pct ?? 0}%` : `${(x && x.pct) ?? 0}%`);
            const isPinnedPair = pinned.length === 2 && pinned.includes(a) && pinned.includes(b);
            return (
              <div className="mx-5 mt-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mineral-compare-panel">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Compare target zones</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <select data-testid="mineral-compare-a" value={a} onChange={(e) => setCmpA(e.target.value)}
                      className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                      {names.map((nm) => <option key={nm} value={nm} label={minerals[nm]?.label || nm} />)}
                    </select>
                    <span className="text-slate-400 text-sm font-semibold">vs</span>
                    <select data-testid="mineral-compare-b" value={b} onChange={(e) => setCmpB(e.target.value)}
                      className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                      {names.map((nm) => <option key={nm} value={nm} label={minerals[nm]?.label || nm} />)}
                    </select>
                    <Button size="sm" variant="outline" data-testid="mineral-compare-pin" disabled={pinBusy || a === b}
                      onClick={() => pinCompare(a, b)}
                      className={`h-9 text-xs ${isPinnedPair ? "border-[#C20511] text-white bg-[#FFFFFF]/10" : "border-slate-300"}`}
                      title="Use this pair on the Master PDF comparison page">
                      <Pin size={13} className="mr-1.5" style={isPinnedPair ? { color: "#C20511" } : undefined} />
                      {isPinnedPair ? "In report" : "Pin to report"}
                    </Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5 p-5">
                  {[["a", a], ["b", b]].map(([side, nm]) => {
                    const d = minerals[nm] || {};
                    const z = d.zones || {};
                    return (
                      <div key={side} data-testid={`mineral-compare-col-${side}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Gem size={15} style={{ color: "#C20511" }} />
                          <span className="font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{d.label || nm}</span>
                          {d.group && <span className="text-[11px] text-slate-400">· {d.group}</span>}
                        </div>
                        {d.maps?.target_zones ? (
                          <img src={d.maps.target_zones} alt={`${nm} target zones`} onClick={() => setZoom(d.maps.target_zones)}
                            data-testid={`mineral-compare-map-${side}`}
                            className="w-full rounded-lg border border-slate-200 cursor-zoom-in hover:border-[#C20511] transition-colors" />
                        ) : (
                          <div className="text-xs text-slate-400 py-10 text-center border border-dashed border-slate-200 rounded-lg">No target-zone map</div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <ZoneChip tier="High" z={z.high || { pct: 0 }} />
                          <ZoneChip tier="Medium" z={z.medium || { pct: 0 }} />
                          <ZoneChip tier="Low" z={z.low || { pct: 0 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-5 pb-5">
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs" data-testid="mineral-compare-table">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Potential tier</th>
                          <th className="text-right px-3 py-2 font-semibold">{minerals[a]?.label || a}</th>
                          <th className="text-right px-3 py-2 font-semibold">{minerals[b]?.label || b}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiers.map(([tier, key]) => (
                          <tr key={tier} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: TIER[tier].c }}>
                                <span className="h-2 w-2 rounded-full" style={{ background: TIER[tier].c }} />{tier}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-700">{fmt((minerals[a]?.zones || {})[key])}</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-700">{fmt((minerals[b]?.zones || {})[key])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* results */}
          <div className="p-5 space-y-6" data-testid="mineral-results">
            {combined && combined.maps && (
              <CombinedCard combined={combined} onZoom={setZoom} />
            )}
            {processing && !names.length && (
              <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto mb-3 animate-spin" /> Running multi-mineral evidence fusion…</div>
            )}
            {!processing && !names.length && (
              <div className="py-16 text-center text-slate-400" data-testid="mineral-empty">Pick one or more minerals above and click <span className="font-semibold text-slate-600">Analyse</span> to generate per-mineral target zones.</div>
            )}
            {names.map((name) => {
              const d = minerals[name];
              const z = d.zones || {};
              const tc = d.target_counts || {};
              const cors = d.corridors || [];
              return (
                <div key={name} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`mineral-card-${name.replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Gem size={16} style={{ color: "#C20511" }} />
                      <span className="font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{d.label || name}</span>
                      {d.group && <span className="text-[11px] text-slate-400">· {d.group}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                      {["High", "Medium", "Low"].map((t) => (
                        <span key={t} className={`px-2 py-0.5 rounded-full border ${TIER[t].bd} ${TIER[t].bg} ${TIER[t].tx}`}>{tc[t] || 0} {t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-5 grid lg:grid-cols-2 gap-5">
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <ZoneChip tier="High" z={z.high || { pct: 0 }} />
                        <ZoneChip tier="Medium" z={z.medium || { pct: 0 }} />
                        <ZoneChip tier="Low" z={z.low || { pct: 0 }} />
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Ranked target coordinates</div>
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                          <table className="w-full text-xs" data-testid={`mineral-table-${name.replace(/\s+/g, "-")}`}>
                            <thead className="bg-slate-50 text-slate-500">
                              <tr>
                                <th className="text-left px-2 py-1.5 font-semibold">#</th>
                                <th className="text-left px-2 py-1.5 font-semibold">Target</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Lon</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Lat</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Score</th>
                                <th className="text-left px-2 py-1.5 font-semibold">Tier</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(d.top_targets || []).map((t, i) => (
                                <tr key={t.target_id || i} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                                  <td className="px-2 py-1.5 font-medium text-slate-700 truncate max-w-[120px]">{t.name}</td>
                                  <td className="px-2 py-1.5 text-right font-mono text-slate-600">{t.lon}</td>
                                  <td className="px-2 py-1.5 text-right font-mono text-slate-600">{t.lat}</td>
                                  <td className="px-2 py-1.5 text-right font-bold text-slate-800">{t.score}</td>
                                  <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${TIER[t.tier].c}22`, color: TIER[t.tier].c }}>{t.tier}</span></td>
                                </tr>
                              ))}
                              {!(d.top_targets || []).length && (
                                <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">No discrete targets above threshold.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {cors.length > 0 && (
                        <div data-testid={`mineral-corridors-${name.replace(/\s+/g, "-")}`}>
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Corridor potential &amp; indicative tonnage</div>
                          <div className="overflow-hidden rounded-lg border border-slate-200">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-semibold">Corridor</th>
                                  <th className="text-left px-2 py-1.5 font-semibold">Orient.</th>
                                  <th className="text-right px-2 py-1.5 font-semibold">L×W (km)</th>
                                  <th className="text-right px-2 py-1.5 font-semibold">Indic. HM</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cors.map((c) => (
                                  <tr key={c.id} className="border-t border-slate-100">
                                    <td className="px-2 py-1.5 font-bold text-slate-800">{c.id}</td>
                                    <td className="px-2 py-1.5 text-slate-600">{c.orientation || "-"}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{c.length_km}×{c.width_km}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-700">
                                      {fmtHMRange(c.hm_mt_low, c.hm_mt_high)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-1">
                            {cors.length} corridor(s) · aggregate indicative HM {fmtHMRange(
                              cors.reduce((s, c) => s + (c.hm_mt_low || 0), 0),
                              cors.reduce((s, c) => s + (c.hm_mt_high || 0), 0)
                            )} (conceptual, not a Resource).
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[["target_zones", "Target Zones (High/Med/Low)"], ["prospectivity", "Prospectivity"]].map(([k, label]) => (
                        d.maps?.[k] ? (
                          <figure key={k} className="cursor-zoom-in" onClick={() => setZoom(d.maps[k])} data-testid={`mineral-map-${name.replace(/\s+/g, "-")}-${k}`}>
                            <img src={d.maps[k]} alt={label} className="w-full rounded-lg border border-slate-200 hover:border-[#C20511] transition-colors" />
                            <figcaption className="text-[10px] text-slate-500 mt-1 text-center">{label}</figcaption>
                          </figure>
                        ) : null
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {zoom && (
          <div className="absolute inset-0 z-50 bg-black/80 grid place-items-center p-6" onClick={() => setZoom(null)} data-testid="mineral-map-zoom">
            <img src={zoom} alt="map" className="max-h-full max-w-full rounded-lg shadow-2xl" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
